import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import path from 'path';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Link as MuiLink, Portal, Stack } from '@mui/material';
import {
	GridColDef,
	GridFilterModel,
	GridInitialState,
	GridPaginationModel,
	GridSlotProps,
	GridSlots,
	PropsFromSlot,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, GridActionsCellItem, WeekToggleButtonGroup } from '~components';
import { pagePathSegments } from '~constants';
import { DayOfWeekEnum } from '~enums';
import { BaseReference, WeeklySchedule } from '~interfaces';
import { MutationRefProps } from '~interfaces/refProps';
import { time24NoSecondsFormatter } from '~utils/dateUtils';

import SkcSchedule from '../../interfaces/skcSchedule';
import SkcSchedulesService from '../../services/skcSchedulesService';

const toolbarIdString = 'topology-participant-toolbar';

const service = new SkcSchedulesService();

interface SchedulesDataGridProps {
	onEdit?: (value: BaseReference) => void;
	onDelete?: (value: BaseReference) => void;
	onCreate?: () => void;
}

const SchedulesDataGrid = forwardRef<MutationRefProps, SchedulesDataGridProps>(
	({ onEdit, onDelete, onCreate }, ref) => {
		const { t } = useTranslation();

		const columns = useMemo<GridColDef<SkcSchedule>[]>(
			() => [
				{
					field: 'id',
					headerName: 'ID',
					renderCell: (params) => (
						<MuiLink component={ReactRouterLink} to={params.value.toString()}>
							{params.value}
						</MuiLink>
					),
					width: 80,
					hideable: false,
				},
				{
					field: 'label',
					headerName: t('ui.label.name'),
					flex: 1,
				},
				{
					field: 'description',
					headerName: t('ui.label.description'),
					valueFormatter: (value: string) => value ?? '-',
					flex: 1,
				},
				{
					field: 'open',
					headerName: t('ui.open'),
					valueGetter: (_, row) => row.schedule,
					valueFormatter: (value: WeeklySchedule) =>
						value.isOpenAllDay ?
							t('ui.allDay')
						:	`${time24NoSecondsFormatter.format(value.period?.start)} - ${time24NoSecondsFormatter.format(value.period?.end)}`,
					flex: 1,
				},
				{
					field: 'organisation',
					headerName: t('organisation'),
					// valueFormatter: (value: BaseReference) => value?.label ?? '-',
					renderCell: (params) =>
						params.value != null ?
							<MuiLink
								component={ReactRouterLink}
								to={`/${path.join(pagePathSegments.Organisations, params.value.id.toString(), pagePathSegments.Summary)}`}
							>
								{params.value.label}
							</MuiLink>
						:	'-',
					flex: 1,
				},
				{
					field: 'daysOfWeek',
					headerName: t('daysOfWeek'),
					valueOptions: Object.values(DayOfWeekEnum),
					valueGetter: (_, row) => row.schedule.daysOfWeek,
					renderCell: (params) => (
						<WeekToggleButtonGroup disabled size='small' value={params.value} />
					),
					type: 'singleSelect',
					flex: 1,
				},
				{
					field: 'actions',
					type: 'actions',
					display: 'text',
					width: 50,
					resizable: false,
					align: 'right',
					hideable: false,
					getActions: (params) => [
						<GridActionsCellItem
							key={`${params.id}-edit`}
							icon={<EditIcon />}
							label={t('ui.edit')}
							onClick={() => onEdit?.({ id: params.id.toString(), label: params.row.label })}
							showInMenu
						/>,
						<GridActionsCellItem
							key={`${params.id}-delete`}
							icon={<DeleteOutlinedIcon color='error' />}
							label={t('ui.delete')}
							onClick={() => onDelete?.({ id: params.id.toString(), label: params.row.label })}
							color='error'
							showInMenu
						/>,
					],
				},
			],
			[t, onDelete, onEdit]
		);

		const [fetchParameters, setFetchParameters] = useState<object>({});

		const { data, isLoading, isValidating, error, mutate } = useSWR(
			fetchParameters.pageSize != null ? [service.basePath, fetchParameters] : null,
			([_, args]) => {
				if (isLoading) {
					service.abortCurrentRequest('Parameters change');
				}

				return service.getSchedules(args);
			},
			{
				keepPreviousData: true,
			}
		);

		const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

		useEffect(() => {
			if (data?.total != null) {
				setTotalCount(data.total);
			}
		}, [data]);

		useImperativeHandle(
			ref,
			() => ({
				mutate: mutate,
			}),
			[mutate]
		);

		const handleStateRestore = (state: GridInitialState) => {
			const searchQuery = state.filter?.filterModel?.quickFilterValues?.[0];
			const organisation = state.filter?.filterModel?.items.find(
				(el) => el.field === 'organisation'
			)?.value;

			setFetchParameters((prev) => ({
				...prev,
				page:
					state.pagination?.paginationModel?.page != null ?
						state.pagination.paginationModel.page + 1
					:	undefined,
				pageSize: state.pagination?.paginationModel?.pageSize,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		const handlePaginationChange = (value: GridPaginationModel) => {
			setFetchParameters((prev) => ({
				...prev,
				page: value.page + 1,
				pageSize: value.pageSize,
			}));
		};

		const handleFilterModelChange = (model: GridFilterModel) => {
			const searchQuery = model.quickFilterValues?.[0];
			const organisation = model?.items.find((el) => el.field === 'organisation')?.value;

			setFetchParameters((prev) => ({
				...prev,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		return (
			<Stack
				spacing={2}
				sx={{
					display: 'flex',
					height: 1,
				}}
			>
				<Box
					id={toolbarIdString}
					sx={{
						minHeight: 48,
					}}
				/>
				<DataGrid
					disableColumnSorting
					snapshotKey='accs'
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					loading={isLoading || isValidating}
					error={error}
					onPaginationModelChange={handlePaginationChange}
					onFilterModelChange={handleFilterModelChange}
					onStateRestore={handleStateRestore}
					slots={{
						toolbar: Toolbar as GridSlots['toolbar'],
					}}
					slotProps={{
						toolbar: {
							onCreate: onCreate,
						} as GridSlotProps['toolbar'],
						noRowsOverlay: {
							title: t('noResourceFound', { resource: t('schedules') }),
						},
						noResultsOverlay: {
							title: t('noResourceFound', { resource: t('schedules') }),
						},
					}}
					sx={{ flexGrow: 1, flexBasis: 200 }}
				/>
			</Stack>
		);
	}
);

interface ToolbarProps extends PropsFromSlot<GridSlots['toolbar']> {
	onCreate?: () => void;
}

const Toolbar = (props: ToolbarProps) => {
	const { t } = useTranslation();

	return (
		<Portal container={() => document.getElementById(toolbarIdString)}>
			<DataGridToolbar organisationSyncMode='url'>
				<Button variant='contained' onClick={() => props.onCreate?.()}>
					{t('addResource', { resource: t('schedule') })}
				</Button>
			</DataGridToolbar>
		</Portal>
	);
};

export default SchedulesDataGrid;
